import { CREATE_AUDIT_EVENT } from "adapters/ehr.service";
import {
  END_ASYNC_RECORDING,
  END_ASYNC_STATUS,
  END_RECORDING,
  GET_STT_TOKEN,
  PAUSE_RECORDING,
  RECORD_EVENTS,
  START_RECORDING,
} from "adapters/recognizer.service";
import { setEncounterIdInParams } from "config/helpers/encounterReferenceExtractor";
import { LIMITS } from "config/helpers/license_helper";
import { initializeSpeechService } from "config/helpers/speechTranscriptionHelpers/initialize_speech_helper";
import i18n from "multilingual";
import { toast } from "react-toastify";
import {
  leftPanelReset,
  rightPanelReset,
} from "redux/reducers/EncounterResetSlice";
import { recognizerManager } from "screens/patientInfo/classes/RecognizerManager";
import { getCurrentPlan } from "screens/store/storeHelper";
import { constants } from "../constants/redux-constants";
import { fetchUsage } from "./PlanUsageActions";
import { setRenderTabValue } from "./ScribeAcitons";
import { progressStatus } from "./loaderActions";
import { TASK_STATUS } from "config/constants/constants";
import { setConsent } from "./Consent";

var tokenRefreshTimeoutId = null;

export const initializeSttTokenService = (firstTime = true) => {
  return async (dispatch) => {
    try {
      const tokenResponse = await GET_STT_TOKEN();
      dispatch({
        type: constants.RECOGNIZER.SET_STT_TOKEN,
        payload: tokenResponse,
      });

      if (!firstTime) {
        recognizerManager.recognizer.authorizationToken = tokenResponse;
        recognizerManager.speechConfig.authorizationToken = tokenResponse;
      }

      tokenRefreshTimeoutId = setTimeout(() => {
        dispatch(initializeSttTokenService(false));
      }, 7 * 60 * 1000);
    } catch (error) {
      console.error("Error refreshing token:", error);
      return null;
    }
  };
};
export const startRecording = (payload) => {
  return async (dispatch, getState) => {
    let micPermissionStatus = null;
    const handleError = (errorMessage) => {
      destroySttTokenTimeout();
      recognizerManager.stopTranscription();
      dispatch({
        type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
      });
      dispatch({
        type: constants.RECOGNIZER.START_RECORDING_LOADING,
        payload: false,
      });
      if (errorMessage) {
        toast.error(errorMessage);
      }
    };

    try {
      dispatch(progressStatus(true));
      try {
        const permissions = await navigator.permissions.query({
          name: "microphone",
        });
        micPermissionStatus = permissions.state;
      } catch (error) {
        micPermissionStatus = "prompt";
      }
      if (micPermissionStatus === "denied") {
        handleError(i18n.t("error_messages.MIC_BLOCKED"));
        return;
      }
      if (micPermissionStatus !== "granted") {
        try {
          await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch (error) {
          handleError(i18n.t("error_messages.ALLOW_MIC"));
          return;
        }
      }

      // 🔹 Step 2: Initialize STT service
      await dispatch(initializeSttTokenService()).catch(() => {
        throw new Error("Failed to initialize STT service");
      });

      const {
        token,
        language: { code },
      } = getState().recognizer;

      if (!token) {
        handleError(i18n.t("error_messages.FAILED_INITIALIZE"));
        return;
      }

      // 🔹 Step 3: Initialize Speech Service
      initializeSpeechService(dispatch, payload);
      const currentPlan = getCurrentPlan();
      const planUsage = getState()?.planUsage?.usagePayload;
      const { patientObj } = getState().patientObj;
      const { practitionerData, organisationData, currentUserSettings } =
        getState().auth;
      const encounterObj = getState().encounterObj;

      const startRecordingPayload = {
        patientId: patientObj?.id,
        practitionerId: practitionerData?.id,
        organizationId: organisationData?.id,
        language: code,
        cdss:
          currentPlan?.toLowerCase() === "lite" ||
            currentUserSettings?.display?.CDSSHidden ||
            LIMITS[currentPlan]?.cdss <= planUsage?.cdss
            ? false
            : true,
        ...(encounterObj?.encounter?.id && {
          encounterId: encounterObj.encounter.id,
        }),
      };

      let conversationAndEncounterIds;
      try {
        // Fetch updated usage
        await dispatch(fetchUsage());
        conversationAndEncounterIds = await START_RECORDING(startRecordingPayload);
      } catch (error) {
        handleError("Failed to start recording. Please try again.");
        return;
      }

      if (!conversationAndEncounterIds?.id) {
        handleError("Failed to initialize recording session");
        return;
      }
      dispatch({
        type: constants.RECOGNIZER.START_RECORDING,
        payload: { conversationId: conversationAndEncounterIds.id },
      });
      setEncounterIdInParams(conversationAndEncounterIds?.encounterId);
      // dispatch(fetchUsage());
    } catch (error) {
      handleError("An error occurred while starting the recording");
      console.debug("Recording error:", error);
    } finally {
      dispatch(progressStatus(false));
    }
  };
};

export const pauseRecording = (payload) => {
  const { recognitionEvents, setRecognitionEvents, conversationId } = payload;

  return async (dispatch) => {
    dispatch({
      type: constants.RECOGNIZER.PAUSE_RECORDING,
    });
    recognizerManager.pauseTranscription();
    destroySttTokenTimeout();

    let isRecordingPaused = await PAUSE_RECORDING({
      recognitionEvents,
      conversationId,
    });

    // if (isRecordingPaused) {
    setRecognitionEvents([]);
    return isRecordingPaused;
    // } else {
    // dispatch({
    //   type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
    //   payload: true,
    // });
    // }
  };
};

export const resumeRecording = (payload) => {
  const {
    recognitionEvents,
    setRecognitionEvents,
    conversationId,
    handleTranscribed,
  } = payload;

  return async (dispatch) => {
    const handleError = (errorMessage) => {
      destroySttTokenTimeout();
      recognizerManager.stopTranscription();
      dispatch({
        type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
      });
      dispatch({
        type: constants.RECOGNIZER.START_RECORDING_LOADING,
        payload: false,
      });
      if (errorMessage) {
        toast.error(errorMessage);
      }
    };
    let micPermissionStatus = null;
    try {
      const permissions = await navigator.permissions.query({
        name: "microphone",
      });
      micPermissionStatus = permissions.state;
    } catch (error) {
      micPermissionStatus = "prompt";
    }
    if (micPermissionStatus === "denied") {
      handleError(i18n.t("error_messages.MIC_BLOCKED"));
      return;
    }
    if (micPermissionStatus !== "granted") {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        handleError(i18n.t("error_messages.ALLOW_MIC"));
        return;
      }
    }
    await dispatch(
      initializeSttTokenService(typeof handleTranscribed === "function")
    );

    //If transcribed event are recieved, need to initialize the speech recognizer manager
    if (typeof handleTranscribed === "function")
      initializeSpeechService(dispatch, payload);

    let isRecordingResumed = await RECORD_EVENTS({
      recognitionEvents,
      conversationId,
    });

    // if (isRecordingResumed === "duration_limit_exceeded") {
    //   dispatch(
    //     stopRecording({
    //       conversationId,
    //       recognitionEvents,
    //       setRecognitionEvents,
    //     })
    //   );
    //   return false;
    // }

    if (isRecordingResumed) {
      dispatch({
        type: constants.RECOGNIZER.RESUME_RECORDING,
        payload: { conversationId },
      });

      recognizerManager.resumeTranscription();
      return isRecordingResumed;
    } else {
      dispatch({
        type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
      });
    }
  };
};

export const recordEvents = (payload) => {
  const { recognitionEvents, conversationId } = payload;

  return async (dispatch) => {
    let isRecordingResumed = await RECORD_EVENTS({
      recognitionEvents,
      conversationId,
    });

    if (isRecordingResumed === "duration_limit_exceeded") {
      toast.warning("conversation duration exceeds the allowed limit");
      dispatch(
        stopRecording({
          conversationId,
          recognitionEvents,
          setRecognitionEvents: () => { },
        })
      );
      return false;
    }

    if (!isRecordingResumed?.cdss) {
      dispatch({
        type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
        payload: isRecordingResumed?.cdss,
      });
    } else {
      dispatch({
        type: constants.RECOGNIZER.UPDATE_CDSS_DATA,
        payload: isRecordingResumed?.cdss,
      });
      if (isRecordingResumed?.cdss?.titleCode) {
        dispatch({
          type: constants.RECOGNIZER.SET_CDSS_TITLE,
          payload: isRecordingResumed?.cdss?.titleCode,
        });
      }
    }
  };
};

export const destroySttTokenTimeout = () => {
  if (tokenRefreshTimeoutId) {
    clearTimeout(tokenRefreshTimeoutId);
    tokenRefreshTimeoutId = null;
  }
};

const initiateProgressBar = async (conversationId, dispatch) => {
  return new Promise((resolve, reject) => {
    const interval = 3000;
    let consecutiveFails = 0;

    const pollConversationStatus = async () => {
      try {
        const response = await END_ASYNC_STATUS(
          {
            conversationId,
          },
          dispatch
        );
        const data = response.data;
        if (data.status === TASK_STATUS.COMPLETED) {
          clearInterval(polling);
          resolve(response);
          dispatch(resetProgressState());
        } else {
          if (data.status === TASK_STATUS.RUNNING) {
            dispatch(
              updateProgressState({
                isProgressReady: true,
                progress: Math.round(data.progress),
                progressMessage: data.currentTaskGroupCode.split("@")[1],
                step: data.processedTaskGroups,
                totalSteps: data.totalTaskGroups,
              })
            );
          } else {
            if (data.status === TASK_STATUS.FAILED) {
              clearInterval(polling);
              let errorMessage = "Something went wrong";
              const taskGroups = data.taskGroups.filter(
                (f) => f.code === data?.error?.taskGroupCode
              );
              if (taskGroups.length) {
                const tasks = taskGroups?.[0]?.tasks?.find(
                  (f) => f.code === data?.error?.taskCode
                );
                if (tasks?.error) {
                  errorMessage = tasks?.error?.message;
                }
              }
              reject(new Error(errorMessage));
            }
            consecutiveFails = 0;
            dispatch(resetProgressState());
          }
        }
      } catch (error) {
        // toast.error(error)
        console.error("Error during polling:", error);
        consecutiveFails++;

        if (consecutiveFails >= 3) {
          clearInterval(polling);
          dispatch(resetProgressState());
          reject(new Error("Failed getting conversation status"));
        }
      }
    };

    const polling = setInterval(pollConversationStatus, interval);
  });
};

const refreshPatientHistoryWindow = ({ setRecognitionEvents, dispatch }) => {
  dispatch({
    type: constants.RECOGNIZER.STOP_RECORDING,
  });
  dispatch(leftPanelReset());
  dispatch(rightPanelReset());
  setRecognitionEvents([]);
  dispatch({
    type: constants.RECOGNIZER.START_RECORDING_LOADING,
    payload: false,
  });
};

export const stopRecording = (payload) => {
  const { recognitionEvents, setRecognitionEvents, conversationId, context } =
    payload;
  return async (dispatch, getState) => {
    const reduxState = getState();
    const PractitionerID = reduxState?.auth?.practitionerData?.id;
    const PatientName = reduxState?.patientObj?.patientObj?.name?.[0]?.text;
    try {
      // TODO Need to think of a better way to do this, Without creating seperate redux
      dispatch({
        type: constants.RECOGNIZER.CONVERSATION_ENDING,
      });
      recognizerManager.stopTranscription();
      destroySttTokenTimeout();
      dispatch({
        type: constants.PROGRESS_STATUS,
        payload: true,
      });
      let response = await END_ASYNC_RECORDING(
        {
          recognitionEvents,
          conversationId,
          context,
        },
        dispatch
      );

      let isRecordingEnded = response.status >= 200 && response.status <= 206;

      // if (response.data) {
      //   dispatch(
      //     setRenderTabValue({
      //       label: response.data.soapNote.result?.code?.text,
      //     })
      //   );
      // }

      if (isRecordingEnded) {
        const { data } = await initiateProgressBar(conversationId, dispatch);

        const soapNoteObservationResponse = data?.taskGroups
          ?.find((f) => f?.code === "task_group@notes.soapnote.generate")
          ?.tasks?.find((f) => f?.code === "notes.soapnote.generate")?.result;

        const soapNoteTitle = soapNoteObservationResponse?.code?.text;

        const defaultClinicalNotesGroup = data?.taskGroups?.find(f => f?.code?.includes('task_group@end-conversation-notes.generate'));

        const defaultClinicalNoteTitle = defaultClinicalNotesGroup?.tasks?.[0]?.result?.code?.text;

        toast?.success(`SOAP Note ${i18n.t(`common.GENERATED_SUCCESSFULLY`)} `);
        refreshPatientHistoryWindow({
          setRecognitionEvents,
          dispatch,
        });

        dispatch(
          setRenderTabValue({
            label: defaultClinicalNoteTitle || soapNoteTitle,
          })
        );

        CREATE_AUDIT_EVENT({
          practitionerID: `Practitioner/${PractitionerID}`,
          patientName: PatientName,
          type: { display: "Create", code: "110120" },
          reason: JSON.stringify({ msg_code: "SOAP_NOTE_GENERATED" }),
        });
      } else {
        // toast.warn('An error occurred while generating SOAP Note, saving data in draft')
        refreshPatientHistoryWindow({
          setRecognitionEvents,
          dispatch,
        });
        // dispatch({
        //   type: constants.RECOGNIZER.HAS_ERROR_ENCOUNTERED,
        // });
      }
    } catch (error) {
      if (error?.message) {
        toast.error(error?.message);
      } else if (typeof error === "string") {
        toast.error(error);
      }
      refreshPatientHistoryWindow({
        setRecognitionEvents,
        dispatch,
      });
      console.log(error.message);
    } finally {
      dispatch({
        type: constants.RECOGNIZER.STOP_RECORDING,
      });
      dispatch({
        type: constants.RECOGNIZER.START_RECORDING_LOADING,
        payload: false,
      });
      dispatch({
        type: constants.PROGRESS_STATUS,
        payload: false,
      });
    }
  };
};

export const saveDraft = (payload) => {
  const { recognitionEvents, setRecognitionEvents, conversationId } = payload;

  return async (dispatch, getState) => {
    let isSavedTranscript;
    // TODO once data save with record events then change pause_recording to record_events
    const reduxState = getState();
    const { paused } = reduxState?.recognizer;
    const PractitionerID = reduxState?.auth?.practitionerData?.id;
    const PatientName = reduxState?.patientObj?.patientObj?.name?.[0]?.text;

    if (conversationId && !paused) {
      isSavedTranscript = await PAUSE_RECORDING({
        recognitionEvents,
        conversationId,
      });
    }

    recognizerManager.stopTranscription();
    destroySttTokenTimeout();

    dispatch({
      type: constants.RECOGNIZER.RESET_RECOGNIZER,
    });
    dispatch(setConsent(false));

    CREATE_AUDIT_EVENT({
      practitionerID: `Practitioner/${PractitionerID}`,
      patientName: PatientName,
      type: { display: "Create", code: "110120" },
      reason: JSON.stringify({ msg_code: "DRAFT_ADDED" }),
    });
  };
};

export const autoStartRecording = (payload) => ({
  type: constants.RECOGNIZER.AUTO_START,
  payload: payload,
});
export const zoomCallOngoingChange = (payload) => ({
  type: constants.RECOGNIZER.ZOOM_CALL_ONGOING,
  payload: payload,
});

export const updateProgressState = (payload) => ({
  type: constants.RECOGNIZER.UPDATE_PROGRESS_STATE,
  payload: payload,
});

export const resetProgressState = () => ({
  type: constants.RECOGNIZER.RESET_PROGRESS_STATE,
});
